import React, { useEffect, useState, useRef } from "react";
import apiClientAdmin from "../../../services/apiClientAdmin";
import ParticipantForm from "../Participant-Form/ParticipantForm";
import ParticipantTable from "../Participant-Table/ParticipantTable";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import Dialog from "../FormDialog/Dialog";
import LoadingStyle from "../../Loading/Loading";
import { getHeaders } from "../../ApiHeaders/apiHeaders";
import { datadogRum } from "@datadog/browser-rum";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";

interface ParticipantSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  /** Admin on this page: may create, edit and delete. Otherwise the page is view-only. */
  canDelete?: boolean;
}

const ParticipantSetup: React.FC<ParticipantSetupProps> = ({
  registerTabSwitchGuard,
  canDelete = false,
}) => {
  const [participants, setParticipants] = useState<any[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [iso, setIso] = useState<string[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<any | null>(
    null
  );
  const [selectedRow, setSelectedRow] = useState(null);
  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);

  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };
  useEffect(() => {
    if (registerTabSwitchGuard) {
      registerTabSwitchGuard(() => {
        return new Promise<boolean>((resolve) => {
          if (isFormDirty || isCreateMode) {
            setDialog({
              open: true,
              title: "Unsaved Changes",
              message:
                "You have unsaved changes. Are you sure you want to discard them?",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              onConfirm: () => {
                setIsFormDirty(false);
                setIsCreateMode(false);
                closeDialog();
                resolve(true);
              },
              onCancel: () => {
                closeDialog();
                resolve(false);
              },
            });
          } else {
            resolve(true);
          }
        });
      });
    }
  }, [isFormDirty, isCreateMode]);
  // const handleApiError = (err: any, fallbackTitle: string) => {
  //   let title = fallbackTitle;
  //   let details = 'An unknown error occurred';

  //   if (err.response?.data?.error) {
  //     title = err.response.data.error.title || fallbackTitle;
  //     details = err.response.data.error.error.message || details;
  //   } else if (err.message) {
  //     if (err.message.includes('Network Error') || err.message.includes('timeout')) {
  //       title = 'Network Error';
  //       details = 'Unable to connect to the server. Check your internet connection.';
  //     } else {
  //       details = err.message;
  //     }
  //   }
  //   setError({ title, details });
  //   datadogRum.addError(new Error(details), { title, details });
  // };
  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;
    const errorResponse = err.response?.data?.error;

    const backendMessage =
      errorResponse?.error?.message || errorResponse?.message;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = backendMessage || "The data you submitted is invalid.";
        break;
      case 401:
        title = "Acces Denied";
        details =
          "You're not authorized to access this content. Please log in or check your access permissions.";
        break;
      case 403:
        title = "Access Denied";
        details = "You need permission to access this site.";
        break;
      case 404:
        title = "Not Found";
        details =
          backendMessage || "The requested resource could not be found.";
        break;
      case 500:
        title = "Internal Server Error";
        details =
          backendMessage ||
          "Server error while fetching data. Please try again later.";
        break;
      default:
        if (
          err.message?.includes("Network Error") ||
          err.message?.includes("timeout")
        ) {
          title = "Network Error";
          details =
            "Unable to connect to the server. Check your internet connection.";
        } else {
          title = fallbackTitle;
          details =
            backendMessage || err.message || "An unexpected error occurred.";
        }
        break;
    }
    setError({ title, details });
    datadogRum.addError(err, { title, details });
  };

  const fetchParticipants = async () => {
    datadogRum.addAction("Fetching Participants");
    setLoading(true);
    const headers = await getHeaders();
    try {
      const participantsResponse = await apiClientAdmin.get("/participant/", {
        headers,
      });
      const participants: { company_id: number }[] =
        participantsResponse.data?.response?.data || [];

      const companiesResponse = await apiClientAdmin.get("/company/", { headers });
      const companies: { company_id: number; company_name: string }[] =
        companiesResponse.data?.response?.data || [];

      const companyMap: Record<number, string> = companies.reduce(
        (map: Record<number, string>, company) => {
          map[company.company_id] = company.company_name;
          return map;
        },
        {}
      );

      const updatedParticipants = participants.map((participant) => ({
        ...participant,
        company_name: companyMap[participant.company_id] || "Unknown",
      }));

      setParticipants(updatedParticipants);
    } catch (err) {
      handleApiError(err, "Failed to Fetch Participants");
    } finally {
      setLoading(false);
    }
  };

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchParticipants();
      hasFetchedRef.current = true;
    }
  }, []);

  const handleCreateParticipant = async (newParticipant: any) => {
    datadogRum.addAction(
      `Creating Participant: ${newParticipant.participant_name}`
    );
    const headers = await getHeaders();
    try {
      const participantData = {
        ...newParticipant,
      };

      const response = await apiClientAdmin.post("/participant/", participantData, {
        headers,
      });

      if (response.status === 201 || response.status === 200) {
        fetchParticipants(); // Fetch updated list instead of manually updating the state
      } else {
        console.error("Unexpected response:", response);
      }
      setDialog({
        open: true,
        title: "Success",
        message: `Participant "${participantData.participant_name}" successfully created.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } catch (err) {
      handleApiError(err, "Failed to Create Participant");
    }
  };

  const handleSaveParticipant = async (updatedParticipant: any) => {
    datadogRum.addAction(
      `Updating Participant: ${updatedParticipant.participant_name}`
    );
    const headers = await getHeaders();
    const { participant_id, ...participantBody } = updatedParticipant;

    try {
      const response = await apiClientAdmin.put(
        `/participant/${participant_id}`,
        participantBody,
        { headers }
      );

      if (response.status === 200) {
        setParticipants((prev) =>
          prev.map((participant) =>
            participant.participant_id === participant_id
              ? { ...participant, ...updatedParticipant }
              : participant
          )
        );
      } else {
        console.error("Unexpected response:", response);
      }

      setDialog({
        open: true,
        title: "Success",
        message: `Participant "${updatedParticipant.participant_name}" successfully updated.`,
        confirmButtonText: "Close",
        onConfirm: () => {
          closeDialog();
        },
      });
    } catch (err) {
      handleApiError(err, "Failed to Update Participant");
    }
  };

  const handleDeleteParticipant = async (participantId: string) => {
    const headers = await getHeaders();
  
    try {
      const response = await apiClientAdmin.delete(`/participant/${participantId}`, {
        headers,
      });
  
      if (response.status === 200 || response.status === 204) {
        fetchParticipants();
        setSelectedParticipant(null);
        setSelectedRow(null);
      }
  
      setDialog({
        open: true,
        title: "Success",
        message: "Participant successfully deleted.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Delete Participant");
    }
  };

  // const handleRowSelect = (participant: any) => {
  //   setSelectedParticipant(participant);
  //   setSelectedRow(participant.participant_name);
  // };
  const handleRowSelect = (participant: any) => {
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedParticipant(participant);
          setSelectedRow(participant.participant_name);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedParticipant(participant);
      setSelectedRow(participant.participant_name);
    }
  };

  // const onClearSelection = () => {
  //   setSelectedParticipant(null);
  //   setSelectedRow(null);
  // };
  const onClearSelection = () => {
    if (isFormDirty) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        // confirmButtonText: "Discard Changes",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setSelectedParticipant(null);
          setSelectedRow(null);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedParticipant(null);
      setSelectedRow(null);
    }
  };

  const split = useResizableSplit("lg");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col lg:flex-row bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
    {/* <div className="flex-grow lg:basis-1/2 flex flex-col relative"> */}
    <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
    {loading && (
    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
      <LoadingStyle />
    </div>
  )}
        <ParticipantTable
          participants={participants}
          onEditParticipant={handleSaveParticipant}
          onRowSelect={handleRowSelect}
          onClearSelection={onClearSelection}
          selectedRow={selectedRow}
        />
      </div>
      {/* <div className="flex-grow lg:basis-1/3 flex flex-col h-full"> */}
      {/* <div className="flex-grow flex flex-col lg:basis-1/4 xl:self-stretch "> */}
      
      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
        <ParticipantForm
          onCreateParticipant={handleCreateParticipant}
          companies={companies}
          iso={iso}
          selectedParticipant={selectedParticipant}
          onSaveParticipant={handleSaveParticipant}
          onClearSelection={onClearSelection}
          onDirtyChange={setIsFormDirty}
          onModeChange={(mode) => setIsCreateMode(mode === "create")}
          onDeleteParticipant={handleDeleteParticipant}
          canDelete={canDelete}

        />
      </div>
      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onCancel={dialog.onCancel}
          onConfirm={dialog.onConfirm}
          onClose={closeDialog}
        />
      )}
      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default ParticipantSetup;
